import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/apiUtils';
import Modal from '../Common/Modal';

// Field definitions matching CreateOrderModal
const STAMP_FIELDS = [
  { id: 'article', label: 'Article', type: 'text', required: true },
  { id: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 },
  { id: 'denomination', label: 'Denomination (₹)', type: 'number', required: true },
  { id: 'consideration_amount', label: 'Consideration Amount (₹)', type: 'number', required: false },
  { id: 'delivery_mode', label: 'Delivery Mode', type: 'select', options: ['digital', 'physical', 'both'], required: true, defaultValue: 'digital' },
  { id: 'delivery_address', label: 'Delivery Address', type: 'textarea', required: false },
  { id: 'property_description', label: 'Property Description', type: 'textarea', required: false },
  { id: 'notes', label: 'Notes', type: 'textarea', required: false },
  { id: 'stamp_purchased_by', label: 'Purchased By', type: 'text', required: true },
  { id: 'stamp_paid_by', label: 'Paid By', type: 'text', required: true },
  { id: 'first_party_name', label: 'First Party Name', type: 'text', required: true },
  { id: 'first_party_address', label: 'First Party Address', type: 'textarea', required: false },
  { id: 'first_party_mobile', label: 'First Party Mobile', type: 'text', required: true },
  { id: 'second_party_name', label: 'Second Party Name', type: 'text', required: false },
  { id: 'second_party_address', label: 'Second Party Address', type: 'textarea', required: false },
  { id: 'second_party_mobile', label: 'Second Party Mobile', type: 'text', required: false },
];

const STATES = ['Maharashtra', 'Delhi', 'Karnataka', 'Gujarat', 'Tamil Nadu', 'Rajasthan', 'Uttar Pradesh'];

const LiveStampingServiceConfig = ({ services, onToggle, onAdd, onUpdate, onDelete, onView, onDownloadTemplate, onUploadCsv, onViewCsvUploads }) => {
  const [svcSearch, setSvcSearch] = useState('');
  const [svcStateFilter, setSvcStateFilter] = useState('');
  const [svcStatusFilter, setSvcStatusFilter] = useState(''); // 'active'|'disabled'|''
  const [svcSort, setSvcSort] = useState('name_asc');
  const [svcPage, setSvcPage] = useState(1);
  const SVC_PAGE_SIZE = 6;
  const [svcGoToPage, setSvcGoToPage] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);
  const [editingId, setEditingId] = useState(null);

  // Step 1: Basic service info
  const [newService, setNewService] = useState({
    name: '',
    state: '',
    costPerRequest: 500,
    assignedVendorId: '',
    clientId: ''  // NEW: Required client selection
  });

  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);  // NEW: Clients list

  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        const response = await apiCall('/api/users');
        const userList = Array.isArray(response) ? response : (response.users || []);

        // Filter vendors
        const vendorList = userList.filter(u => u.role === 'vendor');
        setVendors(vendorList);

        // Filter clients
        const clientList = userList.filter(u => u.role === 'client');
        setClients(clientList);

        console.log("Loaded Vendors:", vendorList.length, "Clients:", clientList.length);
      } catch (err) {
        console.error("Failed to fetch users", err);
      }
    };
    fetchUsersData();
  }, []);

  // Step 2: Stamp configurations (array of stamps)
  const [stamps, setStamps] = useState([
    { id: 1, lockedFields: {}, defaultValues: {}, requiredFields: {} }
  ]);
  const [activeStampIndex, setActiveStampIndex] = useState(0);

  const handleEdit = (service) => {
    setEditingId(service.id);
    setNewService({
      name: service?.name || '',
      state: service?.state || '',
      costPerRequest: service?.basePrice || 500,
      assignedVendorId: service?.assigned_vendor_id || '',
      clientId: service?.client_id || ''  // Load client for editing
    });

    // Map existing stamps to local state format (camelCase)
    const existingStamps = (service?.stamps || []).map((s, i) => ({
      id: i + 1,
      lockedFields: s.locked_fields || {},
      defaultValues: s.default_values || {},
      requiredFields: s.required_fields || {},
    }));

    setStamps(existingStamps.length ? existingStamps : [{ id: 1, lockedFields: {}, defaultValues: {}, requiredFields: {} }]);
    setStep(1);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      if (onDelete) await onDelete(id);
    }
  };

  const handleCopyJson = (svc) => {
    const requestBody = {
      service_id: svc.id,
      article: svc.article || "Affidavit",
      denomination: svc.denomination || 100,
      quantity: 1,
      first_party: {
        name: "JOHN DOE",
        mobile: "9876543210"
      },
      second_party: {
        name: "JANE DOE",
        mobile: "9988776655"
      },
      stamp_purchased_by: "JOHN DOE",
      stamp_paid_by: "JOHN DOE",
      delivery_mode: "digital"
    };
    navigator.clipboard.writeText(JSON.stringify(requestBody, null, 2));
    alert('Request body JSON copied to clipboard!');
  };

  const handleToggleLock = (fieldId) => {
    const stamp = stamps[activeStampIndex];
    const newLockedFields = { ...stamp.lockedFields };

    if (newLockedFields[fieldId]) {
      delete newLockedFields[fieldId];
      const newDefaultValues = { ...stamp.defaultValues };
      delete newDefaultValues[fieldId];
      updateStamp(activeStampIndex, { lockedFields: newLockedFields, defaultValues: newDefaultValues });
    } else {
      // Locking a field also clears its required status (locked = pre-filled, never required from client)
      const newRequiredFields = { ...stamp.requiredFields };
      delete newRequiredFields[fieldId];
      newLockedFields[fieldId] = true;
      updateStamp(activeStampIndex, { lockedFields: newLockedFields, requiredFields: newRequiredFields });
    }
  };

  const handleToggleRequired = (fieldId) => {
    const stamp = stamps[activeStampIndex];
    const newRequiredFields = { ...stamp.requiredFields };
    if (newRequiredFields[fieldId]) {
      delete newRequiredFields[fieldId];
    } else {
      newRequiredFields[fieldId] = true;
    }
    updateStamp(activeStampIndex, { requiredFields: newRequiredFields });
  };

  const handleDefaultValueChange = (fieldId, value) => {
    const stamp = stamps[activeStampIndex];
    updateStamp(activeStampIndex, {
      defaultValues: { ...stamp.defaultValues, [fieldId]: value }
    });
  };

  const updateStamp = (index, updates) => {
    setStamps(stamps.map((s, i) => i === index ? { ...s, ...updates } : s));
  };

  const addStamp = () => {
    const newId = stamps.length + 1;
    setStamps([...stamps, { id: newId, lockedFields: {}, defaultValues: {}, requiredFields: {} }]);
    setActiveStampIndex(stamps.length);
  };

  const removeStamp = (index) => {
    if (stamps.length <= 1) return;
    const newStamps = stamps.filter((_, i) => i !== index);
    setStamps(newStamps);
    setActiveStampIndex(Math.min(activeStampIndex, newStamps.length - 1));
  };

  const handleSave = () => {
    // Map local state (camelCase) to API format (snake_case)
    const apiStamps = stamps.map(s => ({
      id: s.id,
      locked_fields: s.lockedFields,
      default_values: s.defaultValues,
      required_fields: s.requiredFields,
    }));

    const payload = {
      name: newService.name || `${newService.state} Live Stamp Service`,
      state: newService.state,
      base_price: newService.costPerRequest,
      platform_fee: 0,
      assigned_vendor_id: newService.assignedVendorId || null,
      client_id: newService.clientId,  // Required: client this service belongs to
      stamps: apiStamps // Send processed stamps
    };

    if (editingId) {
      if (onUpdate) onUpdate(editingId, payload);
    } else {
      onAdd({
        ...payload,
        // Helper fields for UI immediately after add (optional, backend ignores)
        article: apiStamps[0]?.default_values?.article || 'General',
        denomination: apiStamps[0]?.default_values?.denomination || 100,
      });
    }
    resetForm();
  };

  const resetForm = () => {
    setShowModal(false);
    setStep(1);
    setEditingId(null);
    setEditingId(null);
    setNewService({ name: '', state: '', costPerRequest: 500, assignedVendorId: '', clientId: '' });
    setStamps([{ id: 1, lockedFields: {}, defaultValues: {}, requiredFields: {} }]);
    setActiveStampIndex(0);
  };

  const activeStamp = stamps[activeStampIndex];

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <div>
          <h3 className="font-bold text-gray-800">Service Configuration</h3>
          <p className="text-sm text-gray-500">Manage Live Stamping service templates</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Stats Badges */}
          <div className="flex space-x-2">
            <div className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-xs text-blue-600 font-medium">Total:</span>
              <span className="ml-1 text-sm font-bold text-blue-700">{services.length}</span>
            </div>
            <div className="px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
              <span className="text-xs text-green-600 font-medium">Active:</span>
              <span className="ml-1 text-sm font-bold text-green-700">{services.filter(s => s.isEnabled).length}</span>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>New Service</span>
          </button>
        </div>
      </div>

      {/* Filter / search bar */}
      {(() => {
        const uniqueStates = [...new Set(services.map(s => s.state).filter(Boolean))].sort();
        const svcPrice = s => s.stamps?.reduce((sum, st) => sum + ((st.default_values?.denomination || 0) * (st.default_values?.quantity || 1)), 0) || 0;

        let filtered = services
          .filter(s => !svcStateFilter || s.state === svcStateFilter)
          .filter(s => !svcStatusFilter || (svcStatusFilter === 'active' ? s.isEnabled : !s.isEnabled))
          .filter(s => !svcSearch || s.name.toLowerCase().includes(svcSearch.toLowerCase()) || (s.state || '').toLowerCase().includes(svcSearch.toLowerCase()) || (s.client_name || '').toLowerCase().includes(svcSearch.toLowerCase()));

        if (svcSort === 'name_asc')   filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
        if (svcSort === 'name_desc')  filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
        if (svcSort === 'price_asc')  filtered = [...filtered].sort((a, b) => svcPrice(a) - svcPrice(b));
        if (svcSort === 'price_desc') filtered = [...filtered].sort((a, b) => svcPrice(b) - svcPrice(a));

        const totalPages = Math.ceil(filtered.length / SVC_PAGE_SIZE) || 1;
        const safePage = Math.min(svcPage, totalPages);
        const paginated = filtered.slice((safePage - 1) * SVC_PAGE_SIZE, safePage * SVC_PAGE_SIZE);

        return (
          <>
            <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-2 items-end">
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-0.5">Search</label>
                <input type="text" value={svcSearch} onChange={e => { setSvcSearch(e.target.value); setSvcPage(1); }}
                  placeholder="Name, state, client…" className="text-sm border border-gray-300 rounded px-2 py-1 bg-white w-44" />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-0.5">State</label>
                <select value={svcStateFilter} onChange={e => { setSvcStateFilter(e.target.value); setSvcPage(1); }} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                  <option value="">All states</option>
                  {uniqueStates.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-0.5">Status</label>
                <select value={svcStatusFilter} onChange={e => { setSvcStatusFilter(e.target.value); setSvcPage(1); }} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-0.5">Sort</label>
                <select value={svcSort} onChange={e => setSvcSort(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                  <option value="name_asc">Name A→Z</option>
                  <option value="name_desc">Name Z→A</option>
                  <option value="price_asc">Price Low→High</option>
                  <option value="price_desc">Price High→Low</option>
                </select>
              </div>
              {(svcSearch || svcStateFilter || svcStatusFilter) && (
                <button onClick={() => { setSvcSearch(''); setSvcStateFilter(''); setSvcStatusFilter(''); setSvcPage(1); }}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 self-end">Clear</button>
              )}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto min-h-0">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">ID</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Service Name</th>
                    <th className="px-4 py-2.5 text-left font-semibold">State</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Client</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Stamp Duty</th>
                    <th className="px-4 py-2.5 text-center font-semibold">Status</th>
                    <th className="px-4 py-2.5 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {paginated.map(svc => (
                    <tr key={svc.id} onClick={() => onView && onView(svc)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                      <td className="px-4 py-2 text-xs font-mono text-gray-500 whitespace-nowrap">{svc.id}</td>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{svc.name}</td>
                      <td className="px-4 py-2"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded uppercase">{svc.state}</span></td>
                      <td className="px-4 py-2 text-sm text-indigo-600">{svc.client_name || (() => { const c = clients.find(cl => (cl.id || cl._id) === svc.client_id); return c ? (c.company || c.name) : null; })() || '—'}</td>
                      <td className="px-4 py-2 text-sm font-semibold text-gray-800">₹{svcPrice(svc).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={e => { e.stopPropagation(); onToggle(svc.id); }}
                          className={`relative w-9 h-5 rounded-full transition-colors ${svc.isEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                          title={svc.isEnabled ? 'Disable' : 'Enable'}
                        >
                          <span className={`absolute top-1 left-1 w-3 h-3 bg-white rounded-full shadow transition-transform ${svc.isEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                      </td>
                      <td className="px-4 py-2 text-center">
                        {onUploadCsv && (
                          <button
                            onClick={e => { e.stopPropagation(); onUploadCsv(svc); }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                            title="Upload CSV"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v8" />
                            </svg>
                          </button>
                        )}
                        {onDownloadTemplate && (
                          <button
                            onClick={e => { e.stopPropagation(); onDownloadTemplate(svc.id, svc.name, svc.state || 'state'); }}
                            className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
                            title="Download CSV template"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 18h16" />
                            </svg>
                          </button>
                        )}
                        {onViewCsvUploads && (
                          <button
                            onClick={e => { e.stopPropagation(); onViewCsvUploads(svc); }}
                            className="p-1.5 text-gray-400 hover:text-violet-600 rounded-lg hover:bg-violet-50 transition-colors"
                            title="View CSV"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h7" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l7 6-7 6" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h8a2 2 0 012 2v2" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19h8a2 2 0 002-2v-2" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); handleEdit(svc); }}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {paginated.length === 0 && (
                    <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400 italic">No services found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {(() => {
              const start = filtered.length === 0 ? 0 : (safePage - 1) * SVC_PAGE_SIZE + 1;
              const end = Math.min(safePage * SVC_PAGE_SIZE, filtered.length);
              return (
                <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
                  <span>Showing {start} to {end} of {filtered.length} service{filtered.length !== 1 ? 's' : ''}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSvcPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span>Go to page:</span>
                    <input
                      type="number" min="1" max={totalPages}
                      value={svcGoToPage}
                      onChange={e => setSvcGoToPage(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { const p = Math.min(Math.max(1, Number(svcGoToPage)), totalPages); setSvcPage(p); setSvcGoToPage(''); } }}
                      placeholder={String(safePage)}
                      className="w-10 text-center text-xs border border-gray-300 rounded px-1 py-0.5"
                    />
                    <span>/ {totalPages}</span>
                    <button onClick={() => setSvcPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              );
            })()}
          </>
        );
      })()}

      {/* Create Modal */}
      <Modal
        isOpen={showModal}
        onClose={resetForm}
        title={editingId ? 'Edit Service' : (step === 1 ? 'Step 1: Basic Service Info' : 'Step 2: Stamp Configuration')}
        size="max-w-4xl"
      >
        <div className="flex flex-col h-full">
          {/* Modal Header content is now handled by Modal component title prop, but we might want sub-text */}
          <div className="px-6 py-1 bg-gray-50 border-b border-gray-100 mb-4">
            <p className="text-sm text-gray-500">
              {step === 1 ? 'Set the state and pricing' : 'Configure fields for each stamp (lock = fixed, unlock = client fills)'}
            </p>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {step === 1 && (
              <div className="max-w-md mx-auto space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Name *</label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newService.name}
                    onChange={e => setNewService({ ...newService, name: e.target.value })}
                    placeholder="e.g., MH Rental Agreement"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newService.state}
                    onChange={e => setNewService({ ...newService, state: e.target.value })}
                  >
                    <option value="">Select State...</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Client *</label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newService.clientId}
                    onChange={e => setNewService({ ...newService, clientId: e.target.value })}
                  >
                    <option value="">Select Client...</option>
                    {clients.map(c => (
                      <option key={c.id || c._id} value={c.id || c._id}>
                        {c.name} {c.company ? `(${c.company})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">This service will only be visible to the selected client.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign Vendor (Auto-Assignment)</label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newService.assignedVendorId}
                    onChange={e => setNewService({ ...newService, assignedVendorId: e.target.value })}
                    disabled={!newService.state}
                  >
                    <option value="">{newService.state ? '-- Select Vendor --' : 'Select State First'}</option>
                    {vendors
                      .filter(v => v.service_states && v.service_states.includes(newService.state))
                      .map(v => (
                        <option key={v.id} value={v.id}>
                          {v.name} ({v.company || 'Individual'})
                        </option>
                      ))
                    }
                  </select>
                  {newService.state && vendors.filter(v => v.service_states?.includes(newService.state)).length === 0 && (
                    <p className="text-xs text-red-500 mt-1">No vendors found for {newService.state}. Please update vendor profiles.</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Orders for this service will be automatically assigned to this vendor.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Cost Per Request (₹) *</label>
                  <input
                    type="number"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newService.costPerRequest}
                    onChange={e => setNewService({ ...newService, costPerRequest: Number(e.target.value) })}
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">This is the base service cost. Stamp denominations will be added at checkout.</p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                {/* Stamp Tabs */}
                <div className="flex items-center space-x-2 border-b border-gray-200 pb-3">
                  {stamps.map((stamp, index) => (
                    <button
                      key={stamp.id}
                      onClick={() => setActiveStampIndex(index)}
                      className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-all relative ${activeStampIndex === index
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      Stamp {index + 1}
                      {stamps.length > 1 && (
                        <span
                          onClick={(e) => { e.stopPropagation(); removeStamp(index); }}
                          className="ml-2 text-xs hover:text-red-300"
                        >×</span>
                      )}
                    </button>
                  ))}
                  <button
                    onClick={addStamp}
                    className="px-3 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Stamp
                  </button>
                </div>

                {/* Field Configuration for Active Stamp */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {STAMP_FIELDS.map(field => {
                    const isLocked = !!activeStamp.lockedFields[field.id];
                    const isRequired = !!activeStamp.requiredFields[field.id];
                    const defaultValue = activeStamp.defaultValues[field.id] || '';

                    return (
                      <div
                        key={field.id}
                        className={`p-4 rounded-lg border-2 transition-all ${isLocked
                          ? 'bg-indigo-50 border-indigo-200'
                          : 'bg-gray-50 border-dashed border-gray-300'
                          }`}
                      >
                        <div className="flex justify-between items-center mb-2 gap-2">
                          <span className="font-medium text-gray-700 text-sm">
                            {field.label}
                            {isRequired && !isLocked && <span className="text-red-500 ml-1">*</span>}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            {!isLocked && (
                              <button
                                onClick={() => handleToggleRequired(field.id)}
                                className={`px-2 py-1 rounded text-xs font-bold flex items-center space-x-1 ${isRequired
                                  ? 'bg-red-100 text-red-700 border border-red-300'
                                  : 'bg-gray-100 text-gray-500 border border-gray-200'
                                  }`}
                                title={isRequired ? 'Mark as optional' : 'Mark as required'}
                              >
                                <span>{isRequired ? '✱ Required' : 'Optional'}</span>
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleLock(field.id)}
                              className={`px-2 py-1 rounded text-xs font-bold flex items-center space-x-1 ${isLocked
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-200 text-gray-600'
                                }`}
                            >
                              <span>{isLocked ? '🔒' : '🔓'}</span>
                              <span>{isLocked ? 'Locked' : 'Unlock'}</span>
                            </button>
                          </div>
                        </div>

                        {isLocked ? (
                          <div className="mt-2">
                            {field.type === 'select' ? (
                              <select
                                className="w-full p-2 border border-indigo-200 rounded bg-white text-sm"
                                value={defaultValue}
                                onChange={e => handleDefaultValueChange(field.id, e.target.value)}
                              >
                                <option value="">Select default...</option>
                                {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : field.type === 'textarea' ? (
                              <textarea
                                className="w-full p-2 border border-indigo-200 rounded bg-white text-sm"
                                rows="2"
                                placeholder={`Default ${field.label.toLowerCase()}`}
                                value={defaultValue}
                                onChange={e => handleDefaultValueChange(field.id, e.target.value)}
                              />
                            ) : (
                              <input
                                type={field.type}
                                className="w-full p-2 border border-indigo-200 rounded bg-white text-sm"
                                placeholder={`Default ${field.label.toLowerCase()}`}
                                value={defaultValue}
                                onChange={e => handleDefaultValueChange(field.id, e.target.value)}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-gray-500 italic">
                            {isRequired ? 'Client must fill this field' : 'Client will fill this field (optional)'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-2 border-t border-gray-100 bg-gray-50 flex justify-between mt-auto">
            <div>
              {step === 2 && (
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                >
                  ← Back
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                disabled={step === 1 && (!newService.state || !newService.name)}
                onClick={() => step === 1 ? setStep(2) : handleSave()}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
              >
                {step === 1 ? 'Next: Configure Stamps →' : (editingId ? 'Update Service' : `Create Service`)}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LiveStampingServiceConfig;
